/**
 * ============================================================================
 * WorkbenchFilters — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: top filter bar (criticality / lifecycle status / risk category /
 *          subtype). Each filter is a single-select dropdown with an explicit
 *          "All" option so users can clear without keyboard juggling.
 *
 * A11Y: every <select> has a paired <label>; the row is wrapped in a <form>
 *  with role="search" so AT users hear "Filters search region".
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import styles from './SupplierWorkbench.module.css';
import type {
  RiskCategory,
  SupplierCriticality,
  SupplierLifecycleStatus,
  SupplierSubtype,
  SuppliersQueryParams,
} from './useSuppliersQuery';

const CRITICALITY: SupplierCriticality[] = ['CRITICAL', 'MAJOR', 'MINOR', 'LOW'];
const RISK: RiskCategory[] = ['CRITICAL', 'MAJOR', 'MINOR', 'LOW'];
const LIFECYCLE: SupplierLifecycleStatus[] = [
  'PENDING_REQUEST',
  'QUESTIONNAIRE_ISSUED',
  'AUDIT_SCHEDULED',
  'ACTIVE',
  'RESTRICTED',
  'DISQUALIFIED',
  'ARCHIVED',
];
const SUBTYPE: SupplierSubtype[] = [
  'API',
  'EXCIPIENT',
  'PACKAGING',
  'EQUIPMENT',
  'CONTRACT_MFG',
  'CONTRACT_LAB',
  'CALIBRATION',
  'CONSUMABLE',
  'IT',
  'OTHER',
];

export interface WorkbenchFiltersProps {
  value: SuppliersQueryParams;
  onChange: (next: SuppliersQueryParams) => void;
}

export function WorkbenchFilters({ value, onChange }: WorkbenchFiltersProps) {
  const { t } = useTranslation();

  function update<K extends keyof SuppliersQueryParams>(
    key: K,
    next: SuppliersQueryParams[K] | '',
  ) {
    const merged = { ...value };
    if (next === '' || next == null) {
      delete merged[key];
    } else {
      merged[key] = next as SuppliersQueryParams[K];
    }
    onChange(merged);
  }

  function clear() {
    onChange({});
  }

  const hasAny =
    !!value.criticality ||
    !!value.lifecycle_status ||
    !!value.risk_category ||
    !!value.subtype ||
    !!value.q;

  return (
    <form
      role="search"
      className={styles.filterBar}
      aria-label={t('supplier.workbench.filters.regionAria', {
        defaultValue: 'Supplier filters',
      })}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className={styles.filterField}>
        <label
          htmlFor="sup-f-crit"
          className={styles.filterLabel}
        >
          {t('supplier.workbench.filters.criticality', {
            defaultValue: 'Criticality',
          })}
        </label>
        <select
          id="sup-f-crit"
          className={styles.filterSelect}
          value={value.criticality ?? ''}
          onChange={(e) =>
            update('criticality', e.target.value as SupplierCriticality | '')
          }
        >
          <option value="">
            {t('supplier.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {CRITICALITY.map((c) => (
            <option key={c} value={c}>
              {t(`supplier.criticality.${c}`, { defaultValue: c })}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label htmlFor="sup-f-life" className={styles.filterLabel}>
          {t('supplier.workbench.filters.lifecycle', {
            defaultValue: 'Lifecycle status',
          })}
        </label>
        <select
          id="sup-f-life"
          className={styles.filterSelect}
          value={value.lifecycle_status ?? ''}
          onChange={(e) =>
            update(
              'lifecycle_status',
              e.target.value as SupplierLifecycleStatus | '',
            )
          }
        >
          <option value="">
            {t('supplier.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {LIFECYCLE.map((s) => (
            <option key={s} value={s}>
              {t(`supplier.lifecycle.${s}`, { defaultValue: s })}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label htmlFor="sup-f-risk" className={styles.filterLabel}>
          {t('supplier.workbench.filters.risk', {
            defaultValue: 'Risk category',
          })}
        </label>
        <select
          id="sup-f-risk"
          className={styles.filterSelect}
          value={value.risk_category ?? ''}
          onChange={(e) =>
            update('risk_category', e.target.value as RiskCategory | '')
          }
        >
          <option value="">
            {t('supplier.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {RISK.map((r) => (
            <option key={r} value={r}>
              {t(`supplier.risk.${r}`, { defaultValue: r })}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label htmlFor="sup-f-sub" className={styles.filterLabel}>
          {t('supplier.workbench.filters.subtype', {
            defaultValue: 'Subtype',
          })}
        </label>
        <select
          id="sup-f-sub"
          className={styles.filterSelect}
          value={value.subtype ?? ''}
          onChange={(e) =>
            update('subtype', e.target.value as SupplierSubtype | '')
          }
        >
          <option value="">
            {t('supplier.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {SUBTYPE.map((s) => (
            <option key={s} value={s}>
              {t(`supplier.subtype.${s}`, { defaultValue: s })}
            </option>
          ))}
        </select>
      </div>

      {hasAny && (
        <button
          type="button"
          className={styles.filterClear}
          onClick={clear}
          aria-label={t('supplier.workbench.filters.clearAria', {
            defaultValue: 'Clear all supplier filters',
          })}
        >
          {t('supplier.workbench.filters.clear', { defaultValue: 'Clear' })}
        </button>
      )}
    </form>
  );
}
